import hashConfig from '../hash-config.js';
import {setAreaCoverage} from '@explorer/actions/area-coverage';
import {loadCompareSubjects, setCompareMethod} from '@explorer/actions/compare';
import {loadFolderContents} from '@explorer/actions/folders';
import {applyFilters} from '@explorer/actions/item-filters';
import {selectItemTypes} from '@explorer/actions/item-types';
import {changeMapView, setOverlayOn} from '@explorer/actions/map';
import {selectMosaicByName} from '@explorer/actions/mosaics';
import {
  addItemsToOrder,
  addItemsToView,
  updateStatus,
} from '@explorer/actions/results';
import {defineGeometry, setDateRanges} from '@explorer/actions/search';
import {selectSeries, setActiveSeriesId} from '@explorer/actions/series';
import {
  setProcess,
  setProcessColor,
  setTileMode,
} from '@explorer/actions/tiles';
import {setMode, showLeftDrawer} from '@explorer/actions/ui';
import {DRAWERS, SERIES_DELIM, TILE_MODE} from '@explorer/constants';
import {OVERLAYS} from '@explorer/constants/map.js';
import {KEYS as FILTER_KEYS} from '@explorer/util/filters';
import {DAY} from '@explorer/util/intervals';

export const buildActionsFromHash = hashString => {
  const actions = [];
  const hash = {};

  const hashArr = hashString.substring(2).split('/');
  for (let i = 0, ii = hashArr.length; i < ii; i += 2) {
    hash[hashArr[i]] = decodeURIComponent(hashArr[i + 1]);
  }

  for (const key in hash) {
    // deserialize the hash based on the config.
    if (hashConfig[key] && hashConfig[key].deserialize) {
      hash[key] = hashConfig[key].deserialize(hash[key]);
    }
  }

  // Filters
  const filterVals = {};
  let hasFilters = false;
  Object.keys(FILTER_KEYS).forEach(filterName => {
    const filterKey = FILTER_KEYS[filterName];
    if (filterKey in hash) {
      const values = hash[filterKey];
      filterVals[filterKey] = Array.isArray(values)
        ? values.map(x => (isNaN(x) ? x : Number(x)))
        : values;

      hasFilters = true;
    }
  });
  if (hasFilters) {
    actions.push(applyFilters(filterVals));
  }

  // Area coverage
  if ('area-coverage' in hash) {
    actions.push(setAreaCoverage(hash['area-coverage']));
  }

  // Item-Types
  if ('types' in hash) {
    actions.push(selectItemTypes(hash.types));
  }

  if ('process' in hash && hash.process) {
    const [processName, processColor] = hash.process.split(SERIES_DELIM);
    actions.push(setProcess(processName));
    if (processColor && processColor !== 'auto') {
      actions.push(setProcessColor(processColor));
    }
  }

  // Mosaics
  if ('mosaic' in hash) {
    const [seriesId, mosaicName] = hash.mosaic.split('.');
    actions.push(selectSeries(seriesId));
    actions.push(setActiveSeriesId(seriesId));
    actions.push(selectMosaicByName(mosaicName));
    actions.push(showLeftDrawer(DRAWERS.BASEMAPS));
  }

  // UI mode
  if ('mode' in hash) {
    actions.push(setMode(hash.mode));
  }

  // Compare method
  if ('compare-method' in hash) {
    actions.push(setCompareMethod(hash['compare-method']));
  }

  // Map
  if ('center' in hash || 'zoom' in hash) {
    const newMapView = {};
    if ('center' in hash) {
      newMapView.center = hash.center;
    }
    if ('zoom' in hash) {
      newMapView.zoom = hash.zoom;
    }
    actions.push(changeMapView(newMapView));
  }

  if ('ov' in hash) {
    for (const ovName in hash.ov) {
      actions.push(setOverlayOn({name: ovName, on: hash.ov[ovName]}));
    }
  }

  // Search (dates)
  if ('dates' in hash) {
    actions.push(setDateRanges(hash.dates));
  }

  // Search (geometry)
  if ('geometry' in hash && hash.geometry) {
    actions.push(
      defineGeometry({
        geometry: hash.geometry,
        zoomTo: hash.geometry?.zoomTo,
        isGeometryTooComplex: hash.geometry.isGeometryTooComplex,
        fromHash: true,
      })
    );
    actions.push(showLeftDrawer(hash.geometry ? DRAWERS.SEARCH : ''));
    actions.push(updateStatus(!!hash.geometry));
  }

  // Items
  if ('items' in hash) {
    actions.push(
      addItemsToView({
        items: hash.items,
        reset: true,
      })
    );
    actions.push(
      addItemsToOrder({
        items: hash.items,
        reset: true,
      })
    );
  }

  // Lat/Long Center Marker
  if ('centerMarker' in hash) {
    actions.push(setOverlayOn({name: OVERLAYS.CENTER_MARKER, on: true}));
  }

  if ('interval' in hash) {
    const tileMode =
      hash.interval === DAY ? TILE_MODE.RESULTS : TILE_MODE.BASEMAPS;
    actions.push(setTileMode(tileMode));
  }

  if ('comparing' in hash) {
    actions.push(loadCompareSubjects(hash.comparing));
  }

  if ('folder' in hash) {
    if (hash.folder) {
      actions.push(showLeftDrawer(DRAWERS.FOLDERS));
      actions.push(loadFolderContents(hash.folder));
    }
  }

  return actions;
};
